import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiFetch from "../../utlis/apiFetch";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper function to compare objects
const areFiltersEqual = (filters1, filters2) => {
  return (
    filters1.visaCategory === filters2.visaCategory &&
    filters1.countries === filters2.countries &&
    filters1.page === filters2.page &&
    filters1.limit === filters2.limit &&
    filters1.fromDate === filters2.fromDate &&
    filters1.toDate === filters2.toDate &&
    filters1.status === filters2.status &&
    filters1.searchString === filters2.searchString
  );
};

export const fetchApplications = createAsyncThunk(
  "applications/fetchApplications",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState().applications;
      const { visaCategory, countries, page, limit, fromDate, toDate, status, searchString } = state.filters;
      console.log(visaCategory);

      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      // Build query string with all filters
      // Countries comes first as per API requirement
      const params = new URLSearchParams();
      
      if (countries && countries.length > 0) {
        params.append('countries', countries);
      }
      if (status && status !== "All") {
        params.append('status', status);
      }
      if (visaCategory && visaCategory !== "All") {
        params.append('visaCategory', visaCategory);
      }
      if (fromDate) {
        params.append('fromDate', fromDate);
      }
      if (toDate) {
        params.append('toDate', toDate);
      }
      if (searchString && searchString.trim()) {
        params.append('searchString', searchString.trim());
      }
      params.append('page', page || 1);
      params.append('limit', limit || 10);

      const response = await apiFetch(
        `${API_BASE_URL}/api/application/getAllApplications?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch applications");

      return response._data ?? await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const applicationLogSlice = createSlice({
  name: "applications",
  initialState: {
    data: {
      applications: [],
      totalCount: 0,
      totalPages: 1,
    },
    totalPages: 1,
    isLoading: false,
    error: null,
    filters: {
      visaCategory: "",
      countries: "",
      page: 1,
      limit: 10,
      fromDate: "",
      toDate: "",
      status: "All",
      searchString: "",
    },
    lastUsedFilters: {
      visaCategory: "",
      countries: "",
      page: 1,
      limit: 10,
      fromDate: "",
      toDate: "",
      status: "All",
      searchString: "",
    },
    hasInitiallyLoaded: false,
  },
  reducers: {
    clearApplications: (state) => {
      state.data.applications = [];
      state.isLoading = false;
      state.error = null;
      state.totalPages = 1;
      state.hasInitiallyLoaded = false;
    },
    setFilters: (state, action) => {
      const keys = Object.keys(action.payload);
      const onlyPageOrLimit = keys.every((k) => k === "page" || k === "limit");
      let hasFilterChanged = false;

      if (!onlyPageOrLimit) {
        hasFilterChanged = keys.some((key) => {
          if (key === "page" || key === "limit") return false;

          const newVal = action.payload[key];
          const oldVal = state.filters[key];

          if (Array.isArray(newVal) && Array.isArray(oldVal)) {
            if (newVal.length !== oldVal.length) return true;
            return JSON.stringify(newVal) !== JSON.stringify(oldVal);
          }

          return newVal !== oldVal;
        });
      }
      
      // Clear applications when filters change (except page) to prevent showing stale data
      if (!onlyPageOrLimit && hasFilterChanged) {
        state.data.applications = [];
        state.filters.page = 1;
      }
      
      // Update filters
      state.filters = { ...state.filters, ...action.payload };
    },
    setPage: (state, action) => {
      state.filters.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApplications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.isLoading = false;

        state.data = action.payload.body;
        state.totalPages =
          action.payload.body.totalPages ||
          Math.ceil(action.payload.body.totalCount / state.filters.limit) ||
          1;

        // Store the filters that were used for this successful fetch
        state.lastUsedFilters = { ...state.filters };
        state.hasInitiallyLoaded = true;
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearApplications, setFilters, setPage } = applicationLogSlice.actions;
export default applicationLogSlice.reducer;
